import { Application, Container } from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import type { WorldBounds } from "../core/WorldBounds";
import type { Bubble } from "../entities/Bubble";
import type { Divider } from "../entities/Divider";
import type { ForceField } from "../physics/ForceField";
import { OrganicPipeline } from "./OrganicPipeline";
import { WindParticles } from "./WindParticles";
import { MouseTrail } from "./MouseTrail";
import { ScoreSpectrum } from "./ScoreSpectrum";

export class GameRenderer {
  readonly app = new Application();
  pipeline!: OrganicPipeline;
  particles!: WindParticles;
  trail!: MouseTrail;
  spectrum!: ScoreSpectrum;
  private readonly worldLayer = new Container();

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {}

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  async init(host: HTMLElement): Promise<void> {
    await this.app.init({
      background: "#090f29",
      resizeTo: host,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      preference: "webgl",
    });
    host.appendChild(this.app.canvas);
    this.bounds.set(this.app.screen.width, this.app.screen.height);

    this.pipeline = new OrganicPipeline(this.config, this.bounds, this.app.renderer);
    this.particles = new WindParticles(this.config, this.bounds);
    this.trail = new MouseTrail(this.config);
    this.spectrum = new ScoreSpectrum(this.bounds);
    this.worldLayer.addChild(this.pipeline.backdrop.graphics);
    this.worldLayer.addChild(this.particles.view);
    this.worldLayer.addChild(this.pipeline.output);
    this.worldLayer.addChild(this.trail.view);
    this.worldLayer.addChild(this.spectrum.view);
    this.worldLayer.addChild(this.pipeline.debugVectors);
    this.worldLayer.addChild(this.pipeline.debug);
    this.app.stage.addChild(this.worldLayer);

    this.app.renderer.on("resize", (width: number, height: number) => {
      this.bounds.set(width, height);
      this.pipeline.resize(width, height);
      this.particles.resize();
    });
  }

  draw(
    bubbles: readonly Bubble[],
    divider: Divider,
    field: ForceField,
    time: number,
    scoreProgress: number,
    dt: number,
  ): void {
    this.pipeline.render(bubbles, divider, field, time);
    this.spectrum.update(dt, time, divider, field, scoreProgress);
  }
}
